<template>
    <div class="flex flex-row align-items-start gap-2 xl:flex-row lg:flex-row md:flex-row px-3 py-2"
        :class="(screenSize === 'xs' || screenSize === 'sm') ? 'overflow-x-scroll' : ''">
        <SelectButton v-model="set" :options="sets" optionLabel="name" multiple aria-labelledby="multiple">
            <template #option="slotProps">
                <span class="text-xs py-1 white-space-nowrap">{{ slotProps.option.name }}</span>
            </template>
        </SelectButton>
        <Button :label="(screenSize === 'xs' || screenSize === 'sm') ? '' : 'Сбросить'" 
            icon="pi pi-filter" class="mt-1" text rounded size="small" severity="info" @click="clear"/>
    </div>

    <DataTable v-if="!loading && list && total > 0"
        v-model:first="skip"
        :value="list" lazy :rows="rows" v-model:filters="filters" ref="dt" dataKey="id"
        :totalRecords="total" :loading="loading" @sort="onSort($event)"
        @filter="onFilter($event)" filterDisplay="menu" 
        :globalFilterFields="['type', 'status', 'date_created', 'sender_bank_code', 'recipient_bank_code']" 
        :selectAll="selectAll" @select-all-change="onSelectAllChange"
        :rowsPerPageOptions="[5, 10, 20, 50]">

        <Column field="id" >
            <template #header>
                <span class="text-xs">ID</span>
            </template>
            <template #body="{ data }">
                <span class="text-sm flex flex-row" @click="copy(data._id)">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.id }}
                        </span>
                    </span>...
                    <span class="flex pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                </span>
            </template>
        </Column>

        <Column field="type" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
            <template #header>
                <span class="text-xs">Тип</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="types" placeholder="Все" class="p-column-filter" :showClear="false">
                    <template #option="slotProps">
                        <div class="inline-block py-1 px-2 text-xs border-round-md font-semibold border-2 surface-border cursor-pointer text-600 font-medium align-items-center" >
                            <span class="flex">{{getMerchantType(slotProps.option)}}</span>
                        </div>
                    </template>
                </Dropdown>
            </template>
            <template #body="{ data }">
                <div :class="getMerchantClass(data.merchant_type)"
                    class="inline-block py-1 px-1 text-xs border-round-md align-items-center white-space-nowrap" >
                    <span class="flex gap-1"> 
                        {{ getMerchantType(data.merchant_type) }}
                        <i v-if="data.merchant_type === 'get'" class="pi pi-arrow-down text-xs font-medium"/>
                        <i v-if="data.merchant_type === 'send'" class="pi pi-arrow-up text-xs font-medium"/>
                        <i v-if="data.merchant_type === 'both'" class="pi pi-sync text-xs font-medium"/>
                    </span>
                </div>
            </template>
        </Column>

        <Column field="status" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
            <template #header>
                <span class="text-xs">Статус</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="statuses" placeholder="Все" class="p-column-filter" :showClear="false">
                    <template #option="slotProps">
                        <div :class="'status-'+slotProps.option" 
                            class="inline-block py-1 px-2 text-xs border-round-md font-semibold border-2 surface-border cursor-pointer text-white font-medium align-items-center" >
                            <span class="flex">{{getStatusLabel(slotProps.option)}}</span>
                        </div>
                    </template>
                </Dropdown>
            </template>
            <template #body="{ data }">
                <div :class="'status-'+data.status" 
                    class="inline-block py-1 px-1 text-xs border-round-md border-2 surface-border text-white align-items-center white-space-nowrap" >
                    <span class="flex">{{getStatusLabel(data.status)}}</span>
                </div>
            </template>
        </Column>

        <Column>
            <template #header>
                <span class="text-xs">Подробно</span>
            </template>
            <template #body="slotProps">
                <div class="flex flex-row">
                    <Button label="Подробно" @click='detail_page(slotProps.data.id, false)' size="small" class="flex text-xs mr-1 border-round-md" severity="info"/>
                    <Button label="" @click='detail_page(slotProps.data.id, true)' size="small" icon="pi pi-external-link" text class="flex border-round-md" severity="info"/>
                </div>
            </template>
        </Column>

        <Column field="sender_amount">
            <template #header>
                <span class="text-xs">Сумма получения</span>
            </template>
            <template #body="{data}">
                <div class="flex flex-row align-items-center text-xs font-medium white-space-nowrap">
                    <img v-if="data.sender && data.sender.bank_code" :src="getBankImage(data.sender.bank_code)" class="flex mr-2" style="width:20px">
                    <i v-else class="pi pi-wallet flex mr-2 text-sm font-medium text-500" />

                    <span v-if="(data.merchant_type=='get' || data.merchant_type=='both') && data.sender && data.sender.amount && data.sender.amount">
                        {{ get_format_amount(data.sender.amount, data.sender.currency_code) }}
                    </span>

                    <span v-if="data.merchant_type=='send' && data.send && data.send.merchant && data.send.merchant.amount && data.send.merchant.amount">
                        {{  get_format_amount(data.send.merchant.amount, 'usdt') }}
                    </span>
                </div>
            </template>
        </Column>

        <Column field="sender_bank_code" :showFilterMenu="true" :showFilterMatchModes="false" v-if="selectedColumns.includes('банки')">
            <template #header>
                <span class="text-xs">Банк приема</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="bank_list" optionLabel="name" optionValue="code" placeholder="Все" :filter="true" class="p-column-filter" :showClear="true" />
            </template>
            <template #body="{data}">
                <div v-if="data.merchant_type=='get' || data.merchant_type=='both'" class="flex flex-row align-items-center">
                    <img :src="getBankImage(data.sender.bank_code)" class="flex mr-2" style="width:20px">
                    <span class="text-xs flex">{{ getBankTitle(data.sender.bank_code) }}</span>
                </div>
                <div v-else class="flex flex-row align-items-center">
                    <span class="text-xs flex">{{ getBankTitle('internal') }}</span>
                </div>
            </template>
        </Column>

        <Column field="sender_confirm" v-if="selectedColumns.includes('отметки')">
            <template #header>
                <span class="text-xs">Подтвержден контр-агентом</span>
            </template>
            <template #body="{ data }">
                <span v-if="(data.merchant_type=='get' || data.merchant_type=='both') && data.sender && data.sender.confirm!=undefined" :class="(data.sender && data.sender.confirm)? 'bg-green-400 border-round-md font-medium p-1 text-white' : ''" class="text-xs mr-1">
                    {{(data.sender.confirm) ? 'Да' : 'Нет'}}
                </span>
                <span v-if="data.merchant_type=='send' && data.get && data.get.merchant && data.get.merchant.confirm!=undefined" :class="(data.get && data.get.merchant && data.get.merchant.confirm)? 'bg-green-400 border-round-md font-medium p-1 text-white' : ''" class="text-xs mr-1">
                    {{(data.get && data.get.merchant && data.get.merchant.confirm) ? 'Да' : 'Нет'}}
                </span> 
            </template>
        </Column>

        <Column field="rate_new" v-if="selectedColumns.includes('курсы')">
            <template #header>
                <span class="text-xs">Курс биржи</span>
            </template>
            <template #body="{data}">
                <div class="flex flex-row align-items-center text-xs font-medium white-space-nowrap">
                    {{getTotalRate(data)}}
                </div>
            </template>
        </Column>

        <Column field="rate_com" v-if="selectedColumns.includes('курсы')">
            <template #header>
                <span class="text-xs">Курс с комиссиями</span>
            </template>
            <template #body="{data}">
                <div class="flex flex-row align-items-center text-xs font-medium white-space-nowrap">
                    {{getTotalRateComission(data)}}
                </div>
            </template>
        </Column>

        <Column field="recipient_amount">
            <template #header>
                <span class="text-xs">Сумма выплаты</span>
            </template>
            <template #body="{data}">
               <div class="flex flex-row align-items-center text-xs font-medium white-space-nowrap">
                    <img v-if="data.recipient && data.recipient.bank_code" :src="getBankImage(data.recipient.bank_code)" class="flex mr-2" style="width:20px">
                    <i v-else class="pi pi-wallet flex mr-2 text-sm font-medium text-500" />

                    <span v-if="data.merchant_type=='get' && data.send && data.send.merchant && data.send.merchant.amount && data.send.merchant.amount">
                        {{ get_format_amount(data.send.merchant.amount, 'usdt') }} 
                    </span>
                    <span v-if="(data.merchant_type=='send' || data.merchant_type=='both') && data.recipient && data.recipient.amount && data.recipient.amount && data.recipient.currency_code">
                        {{ get_format_amount(data.recipient.amount, data.recipient.currency_code) }} 
                        <span v-if="data.merchant_type=='both'" class="ml-1 text-500">({{ get_format_amount(data.send.merchant.amount, 'usdt') }})</span>
                    </span>
                </div>
            </template>
        </Column>

        <Column field="recipient_bank_code" :showFilterMenu="true" :showFilterMatchModes="false" v-if="selectedColumns.includes('банки')">
            <template #header>
                <span class="text-xs">Банк отправки</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="bank_list" optionLabel="name" optionValue="code" placeholder="Все" :filter="true" class="p-column-filter" :showClear="true" />
            </template>
            <template #body="{data}">
                <div v-if="data.merchant_type=='send' || data.merchant_type=='both'" class="flex flex-row align-items-center">
                    <img :src="getBankImage(data.recipient.bank_code)" class="flex mr-2" style="width:20px">
                    <span class="text-xs flex">{{ getBankTitle(data.recipient.bank_code) }}</span>
                </div>
                <div v-else class="flex flex-row align-items-center">
                    <img :src="getBankImage('bybit')" class="flex mr-2" style="width:20px">
                    <span class="text-xs flex">{{ getBankTitle('bybit') }}</span>
                </div>
            </template>
        </Column>

        <Column field="merchant_confirm" header="" v-if="selectedColumns.includes('отметки')">
            <template #header>
                <span class="text-xs">Подтвержден вами</span>
            </template>
            <template #body="{ data }">
                <div class="text-xs">
                    <span v-if="data.merchant_type=='get'" :class="(data.get.merchant.confirm)? 'bg-green-400 border-round-md font-medium p-1 text-white mr-1' : 'mr-1'">
                        {{(data.get.merchant.confirm) ? 'Да' : 'Нет'}}
                    </span> 
                    <span v-if="data.merchant_type=='send'" :class="(data.send.merchant.confirm)? 'bg-green-400 border-round-md font-medium p-1 text-white' : ''">
                        {{(data.send.merchant.confirm) ? 'Да' : 'Нет'}}
                    </span>
                    <span v-if="data.merchant_type=='both'" :class="(data.send.merchant.confirm)? 'bg-green-400 border-round-md font-medium p-1 text-white' : ''">
                        {{(data.send.merchant.confirm) ? 'Да' : 'Нет'}}
                    </span> 
                </div>
            </template>
        </Column>

        <Column v-if="selectedColumns.includes('отметки')">
            <template #header>
                <span class="text-xs">Дата подтверждения вами</span>
            </template>
            <template #body="{ data }">
                <div class="text-xs">
                    <span v-if="data.merchant_type=='get' && data.get && data.get.merchant && data.get.merchant.confirm_date!=undefined">
                        {{ formatDate(data.get.merchant.confirm_date) }}
                    </span>
                    <span v-if="data.merchant_type=='send' && data.send && data.send.merchant && data.send.merchant.confirm_date!=undefined">
                        {{ formatDate(data.send.merchant.confirm_date) }}
                    </span>
                    <span v-if="data.merchant_type=='both' && data.send && data.send.merchant && data.send.merchant.confirm_date!=undefined">
                        {{ formatDate(data.send.merchant.confirm_date) }}
                    </span>
                </div>
            </template>
        </Column>

        <Column v-if="selectedColumns.includes('комиссии')">
            <template #header>
                <span class="text-xs">Комиссия мерчанта</span>
            </template>
            <template #body="{ data }"> 
                <div class="text-xs font-medium white-space-nowrap">
                    <span v-if="data.merchant_type=='get' && data.get && data.get.merchant && data.get.merchant.reward_amount && data.get.merchant.reward_amount">
                        {{ get_format_amount(data.get.merchant.reward_amount, 'usdt') }}
                    </span>
                    <span v-if="data.merchant_type=='send' && data.send && data.send.merchant && data.send.merchant.reward_amount && data.send.merchant.reward_amount">
                        {{ get_format_amount(data.send.merchant.reward_amount, 'usdt') }}
                    </span>

                    <span v-if="data.merchant_type=='both' && data.get && data.get.merchant && data.get.merchant.reward_amount && data.get.merchant.reward_amount" class="mr-1">
                        {{ get_format_amount(data.get.merchant.reward_amount, 'usdt') }}
                    </span>
                    <span v-if="data.merchant_type=='both'" class="mr-1">+</span>
                    <span v-if="data.merchant_type=='both' && data.send && data.send.merchant && data.send.merchant.reward_amount && data.send.merchant.reward_amount">
                        {{ get_format_amount(data.send.merchant.reward_amount, 'usdt') }}
                    </span>
                </div>
            </template>
            <template #footer="{ data }">
                <div v-if="stat.coms" class="text-xs font-medium">
                    {{ stat.coms }}
                </div>
            </template>
        </Column>

        <Column v-if="selectedColumns.includes('комиссии')">
            <template #header>
                <span class="text-xs">Сумма в USDT</span>
            </template>
            <template #body="{ data }"> 
                <div class="text-xs font-medium white-space-nowrap">
                    <span v-if="data.merchant_type=='get' && data.get && data.get.merchant && data.get.merchant.amount && data.get.merchant.amount">
                        {{ get_format_amount(data.get.merchant.amount, 'usdt') }}
                    </span>
                    <span v-if="data.merchant_type=='send' && data.send && data.send.merchant && data.send.merchant.amount && data.send.merchant.amount">
                        {{ get_format_amount(data.send.merchant.amount, 'usdt') }}
                    </span>
                    <span v-if="data.merchant_type=='both' && data.get && data.get.merchant && data.get.merchant.amount && data.get.merchant.amount" class="mr-1">
                        {{ get_format_amount(data.get.merchant.amount, 'usdt') }}
                    </span>
                </div>
            </template>
            <template #footer="{ data }">
                <div v-if="stat.coms" class="text-xs font-medium">
                    {{ stat.usdt }}
                </div>
            </template>
        </Column>

        <Column v-if="selectedColumns.includes('отметки')">
            <template #header>
                <span class="text-xs">Начислена комиссия сервиса</span>
            </template>
            <template #body="{ data }"> 
                <div class="text-xs white-space-nowrap">
                    <span v-if="data.merchant_type=='get' && data.get && data.get.merchant && data.get.merchant.reward_paid!=undefined">
                        <span :class="(data.get.merchant.reward_paid) ? 'bg-green-400 border-round-md font-medium p-1 text-white' : ''">
                            {{(data.get.merchant.reward_paid) ? 'Да' : 'Нет'}} 
                        </span>
                    </span>
                    <span v-if="(data.merchant_type=='send' || data.merchant_type=='both') && data.send && data.send.merchant && data.send.merchant.reward_paid!=undefined">
                        <span :class="(data.send.merchant.reward_paid) ? 'bg-green-400 border-round-md font-medium p-1 text-white' : ''">
                            {{(data.send.merchant.reward_paid) ? 'Да' : 'Нет'}} 
                        </span>
                    </span>
                </div>
            </template>
        </Column>

        <Column field="total_amount" v-if="selectedColumns.includes('комиссии')">
            <template #header>
                <span class="text-xs">Сумма с комиссиями</span>
            </template>
            <template #body="{data}">
                <div class="flex flex-row align-items-center text-xs font-medium white-space-nowrap">
                    {{ getFullAmount(data) }}
                </div>
            </template>
        </Column>

        <Column v-if="selectedColumns.includes('комиссии')">
            <template #header>
                <span class="text-xs">Комиссия банка</span>
            </template>
            <template #body="{ data }"> 
                <div class="text-xs font-medium white-space-nowrap">
                    <span v-if="data.merchant_type=='get'">
                        0 USDT
                    </span>
                    <span v-if="(data.merchant_type=='send' || data.merchant_type=='both') && data.send && data.send.merchant && data.send.merchant.bank_amount && data.send.merchant.bank_amount">
                        {{ get_format_amount(data.send.merchant.bank_amount, 'usdt') }}
                    </span>
                </div>
            </template>
            <template #footer="{ data }">
                <div v-if="stat.bank_coms" class="text-xs font-medium">
                    {{ stat.bank_coms }}
                </div>
            </template>
        </Column>

        <Column v-if="selectedColumns.includes('отметки')">
            <template #header>
                <span class="text-xs">Начислена комиссия банка</span>
            </template>
            <template #body="{ data }"> 
                <div class="text-xs white-space-nowrap">
                    <span v-if="data.merchant_type=='get'">
                        Нет
                    </span>
                    <span v-if="(data.merchant_type=='send' || data.merchant_type=='both') && data.send && data.send.merchant && data.send.merchant.bank_paid!=undefined">
                        <span :class="(data.send.merchant.bank_paid) ? 'bg-green-400 border-round-md font-medium p-1 text-white' : ''">
                            {{(data.send.merchant.bank_paid) ? 'Да' : 'Нет'}} 
                        </span>
                    </span>
                </div>
            </template>
        </Column>

        <Column field="date_created" :exportable="false" filterField="date_created" dataType="date" :showFilterMatchModes="false" :showFilterOperator="false">
            <template #header>
                <span class="text-xs">Создан</span>
            </template>
            <template #body="{ data }">
                <span v-if="data.dates && data.dates.created" class="text-xs white-space-nowrap">
                    {{ formatDate(data.dates.created) }}
                </span>
            </template>
            <template #filter="{ filterModel }">
                <Calendar v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" />
            </template>
        </Column>

        <Column >
            <template #header>
                <span class="text-xs">Тайминг</span>
            </template>
            <template #body="{ data }">
                <small v-if="data.merchant_type=='get' && data.sender && data.sender.confirm && data.sender.confirm_date && data.get && data.get.merchant && data.get.merchant.confirm && data.get.merchant.confirm_date">
                    {{ get_timer(data.sender.confirm_date, data.get.merchant.confirm_date)}}
                </small>
                <small v-if="data.merchant_type=='send' && data.send && data.send.merchant && data.send.merchant.confirm && data.send.merchant.confirm_date && data.get && data.get.merchant && data.get.merchant.confirm && data.get.merchant.confirm_date">
                    {{ get_timer(data.get.merchant.confirm_date, data.send.merchant.confirm_date)}}
                </small>
                <small v-if="data.merchant_type=='both' && data.sender && data.sender.confirm && data.sender.confirm_date && data.send && data.send.merchant && data.send.merchant.confirm && data.send.merchant.confirm_date">
                    {{ get_timer(data.sender.confirm_date, data.send.merchant.confirm_date)}}
                </small>
            </template>
        </Column>

        <template #footer>
            <span class="text-sm">Всего {{((list && list.length) ? list.length : 0)}} из {{ total }} ордеров. </span>
        </template>

    </DataTable>
    <div v-if="!loading && list && total>0" class="surface-0 border-round-xl overflow-hidden mb-3">
        <Paginator v-model:first="skip" :rows="rows" :totalRecords="total" :rowsPerPageOptions="[10, 25, 50, 100]"
            @update:rows="onPageRows($event)" @update:first="onPage($event)"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown" />
    </div>
    <div v-if="(!list && total==null) || loading" class="w-full px-2">
        <Skeleton height="2rem" class="mb-3 border-round-xl"/>
    </div>
</template>


<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import {FilterMatchMode,FilterOperator} from '@primevue/core/api';
import BigNumber from 'bignumber.js';
import { BankList } from '@/assets/js/classes/PaymentSystem.js'
import { CurrencyList} from '@/assets/js/classes/Currency.js'

export default {
    props: {
        list: {
            type: Array,
            default: () => []
        },
        count: {
            type: Number,
            default: 10
        },
        loading: {
            type: Boolean,
            default: false
        },
        total: {
            type: Number,
            default: 0
        },
    },
    data() {
        return {
            transfers: [],
            lazyParams: {},
            dt: null,
            selectAll: false,
            types: ['get', 'send', 'both'],
            statuses: ['completed', 'process', 'created', 'closed', 'expired' ],
            filters: {
                type: {value: '', matchMode: 'contains'},
                status: {value: '', matchMode: 'contains'},
                date_created: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
                sender_bank_code: {value: '', matchMode: 'contains'},
                recipient_bank_code: {value: '', matchMode: 'contains'},
            },
            currencies: ['usd', 'eur', 'gbp', 'rub' ],
            paidList: [true, false],
            bank_list: [],
            skip: 0,
            rows: 10,
            setRates: false,
            setBanks: false,
            setComissions: false,
            setMarks: false,
            selectedColumns: [],
            sets: [
                {name: 'Курсы', value: 'курсы'},
                {name: 'Банки', value: 'банки'},
                {name: 'Комиссии', value: 'комиссии'},
                {name: 'Отметки', value: 'отметки'},
            ],
            set: [],
            stat: {
                usdt: null,
                coms: null,
                bank_coms: null,
                total_coms: null,
            },
            banks: [],
            currencies: [],
        }
    },

    methods: {
        get_timer(date1, date2){
            return this.helper.get_timer(date1, date2)
        },
        get_short_id(value){
            return this.helper.get_short_id(value)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        getBankTitle(code){
            return this.helper.getBankTitle(code, this.$store.state.payment.banks, this.lang_)
        },
        getBankTitle(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetTitle(code, this.lang_)
            return (bankCode) ? bankCode : code
        },
        getBankImage(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetImage(code)
            return (bankCode) ? bankCode : code
        },
        get_format_amount(value, currency) {
            return this.helper.format_amount(value, currency)
        },
        detail_page(id, new_tab = false) {
            if(new_tab){
                const routeData = this.$router.resolve({path: '/merchant/transfer/' + id});
                window.open(routeData.href, '_blank');
            } else  this.$router.push({ path: '/merchant/transfer/' + id })
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        formatRate(value) {
            return this.helper.format_rate(value, 2)
        },
        get_currency_label(value) {
            return this.helper.get_currency_label(value)
        },
        get_token_label(value) {
            return this.helper.get_token_label(value)
        },
        getStatusLabel (status) {
            return this.helper.get_severity_status(status, this.lang_)
        },
        cutUserId(that){
            return this.helper.cutUserId(that)
        },
        getMerchantType(type) {
            return (type=='get') ? 'прием' : (type=='send') ? 'выплата' : (type=='both') ? 'круг' : '-'
        },
        getMerchantClass(type){
            return (type=='get') ? 'bg-blue-50 text-blue-600' : (type=='send') ? 'bg-orange-50 text-orange-600' : (type=='both') ? 'bg-green-50 text-green-600' : ''
        },
        onPage(event) {
            this.clearComs()
            this.lazyParams.first = event
            this.skip = event
            this.$emit('onPageChange', event)
        },
        onPageRows(event) {
            this.rows = event
        },
        onFilter() {
            this.clearComs()
            this.$emit('onFilter', this.filters)
        },
        clear(){
            this.set = []
            this.filters = {
                type: {value: '', matchMode: 'contains'},
                status: {value: '', matchMode: 'contains'},
                date_created: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
                sender_bank_code: {value: '', matchMode: 'contains'},
                recipient_bank_code: {value: '', matchMode: 'contains'},
            }
            this.$emit('onFilter', this.filters)
            this.$emit('onPageChange', 0)
            this.skip = 0
            this.clearComs()
        },
        async GetBanks(){
            this.banks = await BankList.GetList(this.$store).then(r=>r)
        },
        async GetCurrencies(){
            this.currencies = await CurrencyList.GetList(this.$store).then(r=>r)
        },
        getTotalRate(data){
            if(!data || !data.merchant_type) return ''

            if(data.merchant_type=='get' && data.get && data.get.merchant && data.get.merchant.amount){
                let getSenderAmount = (data.sender.amount) ? new BigNumber(data.sender.amount) : new BigNumber(0)
                let serviceAmount = (data.service.amount) ? new BigNumber(data.service.amount) : new BigNumber(0)
                let getRate = getSenderAmount.dividedBy(serviceAmount)
                return this.helper.format_rate(getRate, 2)
            }

            if(data.merchant_type=='send' && data.send && data.send.merchant && data.send.merchant.amount){
                let sendMerchantAmount = (data.send.merchant.amount) ? new BigNumber(data.send.merchant.amount) : new BigNumber(0)
                let recipientAmount = (data.recipient.amount) ? new BigNumber(data.recipient.amount) : new BigNumber(0)
                let sendRate = sendMerchantAmount.dividedBy(recipientAmount)
                if(sendRate.isLessThan(1)) sendRate = new BigNumber(1).dividedBy(sendRate)
                return this.helper.format_rate(sendRate, 2)
            }

            if(data.merchant_type=='both' && data.sender && data.sender.amount && data.recipient && data.recipient.amount){
                let getSenderAmount = (data.sender.amount) ? new BigNumber(data.sender.amount) : new BigNumber(0)
                let serviceAmount = (data.service.amount) ? new BigNumber(data.service.amount) : new BigNumber(0)
                let getRate = getSenderAmount.dividedBy(serviceAmount)
                if(getRate.isLessThan(1)) getRate = new BigNumber(1).dividedBy(getRate)

                let sendMerchantAmount = (data.send.merchant.amount) ? new BigNumber(data.send.merchant.amount) : new BigNumber(0)
                let sendRecipientAmount = (data.recipient.amount) ? new BigNumber(data.recipient.amount) : new BigNumber(0)
                
                let sendRate = sendMerchantAmount.dividedBy(sendRecipientAmount)
                if(sendRate.isLessThan(1)) sendRate = new BigNumber(1).dividedBy(sendRate)

                return `${this.helper.format_rate(getRate, 2)} / ${this.helper.format_rate(sendRate, 2)}`
            }

            return ''
        },
        getTotalRateComission(data){
            if(!data || !data.merchant_type) return ''

            if(data.merchant_type=='get' && data.get && data.get.merchant && data.get.merchant.amount){
                let senderAmount = (data.sender.amount) ? new BigNumber(data.sender.amount) : new BigNumber(0)
                let serviceAmount = (data.service.amount) ? new BigNumber(data.service.amount) : new BigNumber(0)
                let rate = senderAmount.dividedBy(serviceAmount) // текущий курс на момент создания перевода
                if(rate.isLessThan(1)) rate = new BigNumber(1).dividedBy(rate)

                let getMerchantAmount = (data.get.merchant.amount) ? new BigNumber(data.get.merchant.amount) : new BigNumber(0)
                let getComission = (data.get.merchant.reward_amount) ? new BigNumber(data.get.merchant.reward_amount) : new BigNumber(0)
                let totalGetAmount = getMerchantAmount.plus(getComission)
                let getMerchantCurAmount = getMerchantAmount.multipliedBy(rate)

                let getRate = getMerchantCurAmount.dividedBy(totalGetAmount)
                return this.helper.format_rate(getRate, 2)
            }

            if(data.merchant_type=='send' && data.send && data.send.merchant && data.send.merchant.amount){
                let sendMerchantAmount = (data.send.merchant.amount) ? new BigNumber(data.send.merchant.amount) : new BigNumber(0)
                let recipientAmount = (data.recipient.amount) ? new BigNumber(data.recipient.amount) : new BigNumber(0)
                let sendComission = (data.send.merchant.reward_amount) ? new BigNumber(data.send.merchant.reward_amount) : new BigNumber(0)
                let sendTotalAmount = sendMerchantAmount.plus(sendComission)
                let sendRate = sendTotalAmount.dividedBy(recipientAmount)
                if(sendRate.isLessThan(1)) sendRate = new BigNumber(1).dividedBy(sendRate)
                return this.helper.format_rate(sendRate, 2)
            }

            if(data.merchant_type=='both' && data.sender && data.sender.amount && data.recipient && data.recipient.amount){
                let senderAmount = (data.sender.amount) ? new BigNumber(data.sender.amount) : new BigNumber(0)
                let serviceAmount = (data.service.amount) ? new BigNumber(data.service.amount) : new BigNumber(0)
                let rate = senderAmount.dividedBy(serviceAmount) // текущий курс на момент создания перевода
                if(rate.isLessThan(1)) rate = new BigNumber(1).dividedBy(rate)

                let getMerchantAmount = (data.get.merchant.amount) ? new BigNumber(data.get.merchant.amount) : new BigNumber(0)
                let getComission = (data.get.merchant.reward_amount) ? new BigNumber(data.get.merchant.reward_amount) : new BigNumber(0)
                let totalGetAmount = getMerchantAmount.plus(getComission)
                let getMerchantCurAmount = getMerchantAmount.multipliedBy(rate)
                let getRate = getMerchantCurAmount.dividedBy(totalGetAmount)
                if(getRate.isLessThan(1)) getRate = new BigNumber(1).dividedBy(getRate)

                let sendMerchantAmount = (data.send.merchant.amount) ? new BigNumber(data.send.merchant.amount) : new BigNumber(0)
                let recipientAmount = (data.recipient.amount) ? new BigNumber(data.recipient.amount) : new BigNumber(0)
                let sendComission = (data.send.merchant.reward_amount) ? new BigNumber(data.send.merchant.reward_amount) : new BigNumber(0)
                let sendTotalAmount = sendMerchantAmount.plus(sendComission)
                let sendRate = sendTotalAmount.dividedBy(recipientAmount)
                if(sendRate.isLessThan(1)) sendRate = new BigNumber(1).dividedBy(sendRate)

                return `${this.helper.format_rate(getRate, 2)} / ${this.helper.format_rate(sendRate, 2)}`
            }

            return ''
        },
        getFullAmount(data){
            if(!data || !data.merchant_type) return ''

            let total = new BigNumber(0)

            if(data.merchant_type=='get' && data.get && data.get.merchant && data.get.merchant.amount){
                let amount = new BigNumber(data.get.merchant.amount)
                let comission = (data.get.merchant.reward_amount) ? new BigNumber(data.get.merchant.reward_amount) : new BigNumber(0)
                total = amount.plus(comission)
                return this.helper.format_amount(total, 'usdt')
            }

            if(data.merchant_type=='send' && data.send && data.send.merchant && data.send.merchant.amount){
                let amount = new BigNumber(data.send.merchant.amount)
                let sendComission = (data.send.merchant.reward_amount) ? new BigNumber(data.send.merchant.reward_amount) : new BigNumber(0)
                total = amount.plus(sendComission)
                return this.helper.format_amount(total, 'usdt')
            }

            if(data.merchant_type=='both' && data.send && data.send.merchant && data.send.merchant.amount && data.get && data.get.merchant && data.get.merchant.amount){
                let amount = new BigNumber(data.send.merchant.amount)
                let getComission = (data.get.merchant.reward_amount) ? new BigNumber(data.get.merchant.reward_amount) : new BigNumber(0)
                let sendComission = (data.send.merchant.reward_amount) ? new BigNumber(data.send.merchant.reward_amount) : new BigNumber(0)
                total = amount.plus(getComission).plus(sendComission)
                return this.helper.format_amount(total, 'usdt')
            }

            return ''
        },
        calcComs(){
            if(!this.list || !this.list.length) return
            let usdt = new BigNumber(0)
            let get_coms = new BigNumber(0)
            let send_coms = new BigNumber(0)
            let coms = new BigNumber(0)
            let bank_coms = new BigNumber(0)
            let total_coms = new BigNumber(0)

            for (let i = 0; i < this.list.length; i++) {
                let v = this.list[i]
                if(v.merchant_type=='get' && v.get && v.get.merchant && v.get.merchant.reward_amount){
                    get_coms = get_coms.plus(new BigNumber(v.get.merchant.reward_amount))
                    total_coms = total_coms.plus(new BigNumber(v.get.merchant.reward_amount))
                    usdt = usdt.plus(new BigNumber(v.get.merchant.amount))
                }
                if((v.merchant_type=='send' || v.merchant_type=='both') && v.send && v.send.merchant){
                    if(v.send.merchant.reward_amount) {
                        send_coms = send_coms.plus(new BigNumber(v.send.merchant.reward_amount))
                        total_coms = total_coms.plus(new BigNumber(v.send.merchant.reward_amount))
                    }
                    if(v.send.merchant.bank_amount) {
                        bank_coms = bank_coms.plus(new BigNumber(v.send.merchant.bank_amount))
                        total_coms = total_coms.plus(new BigNumber(v.send.merchant.bank_amount))
                    }
                    if(v.send.merchant.amount) {
                        usdt = usdt.plus(new BigNumber(v.send.merchant.amount))
                    }
                }
            }
            coms = get_coms.plus(send_coms)
            this.stat.usdt = this.helper.format_amount(usdt, 'usdt')
            this.stat.coms = this.helper.format_amount(coms, 'usdt')
            this.stat.bank_coms = this.helper.format_amount(bank_coms, 'usdt')
            this.stat.total_coms = this.helper.format_amount(total_coms, 'usdt')
        },
        clearComs(){
            this.stat = {
                coms: null,
                bank_coms: null,
                total_coms: null,
            }
        },
    },
    watch: {
        set: {
            handler(val) { 
                this.selectedColumns = []
                for (let i = 0; i < val.length; i++) {
                    let v = val[i].value
                    if(Array.isArray(v)) this.selectedColumns = this.selectedColumns.concat(v)
                    else this.selectedColumns.push(v)
                }
            },
            immediate: true
        },
        rows(val) {
            this.clearComs()
            this.lazyParams.rows = val
            this.$emit('onPageRowsChange', val)
        },
        list: {
            handler(val) { 
                this.calcComs()
            },
            immediate: true
        }
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    computed: {
        lang_() { return (this.$i18n.locale) ? this.$i18n.locale : 'ru' },
        screenSize() { return this.$store.getters.StateScreenSize },
    },
    async mounted() {
        this.lazyParams = {
            first: 0,
            rows: 10,
            sortField: null,
            sortOrder: null,
            filters: this.filters,
        };
        this.calcComs()
        this.GetBanks()
        this.GetCurrencies()
    }
}
</script>